import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';

interface IDateRangePickerHook {
  startDate: DateTime | null,
  endDate: DateTime | null,
  openStartPicker: boolean,
  openEndPicker: boolean,
  setOpenStartPicker: (val: boolean) => void,
  setOpenEndPicker: (val: boolean) => void,
  onMonthChange: (newMonth: DateTime) => Promise<void>,
  onOpenStart: () => Promise<void>;
  onOpenEnd: () => Promise<void>
  onYearChange: (newYear: DateTime) => Promise<void>,
  handleStartAccepted: (newValue: DateTime | null | undefined) => void,
  handleStartDateChange: (newValue: DateTime | null | undefined) => void,
  handleEndDateChange: (newValue: DateTime | null | undefined) => void,
}

function useDateRangePicker(
  onChange: (value: [string | undefined, string | undefined]) => void,
  defaultValue: [DateTime | null, DateTime | null],
  queryFn?: (startDate: string | undefined, endDate: string | undefined) => Promise<any>,
): IDateRangePickerHook {

  const [openStartPicker, setOpenStartPicker] = useState(false);
  const [startDate, setStartDate] = useState<DateTime | null>(defaultValue[0]);
  const [endDate, setEndDate] = useState<DateTime | null>(defaultValue[1]);
  const [openEndPicker, setOpenEndPicker] = useState(false);

  useEffect(() => {
    onChange([startDate?.toFormat('MM-dd-yyyy'), endDate?.toFormat('MM-dd-yyyy')]);
  }, [startDate, endDate, onChange]);

  const handleStartDateChange = (newValue: DateTime | null | undefined) => {
    setStartDate(newValue ?? null);
    if (newValue && endDate && newValue > endDate) {
      setEndDate(null);
      setOpenEndPicker(true);
    }
  };

  const handleStartAccepted = (newValue: DateTime | null | undefined) => {
    if (newValue && !endDate) {
      setEndDate(newValue);
      setOpenEndPicker(true);
    }
  };

  const loadStatsFromDate = async (date: DateTime) => {
    const monthStart = date.startOf('month');
    const monthEnd = date.endOf('month').setLocale('en-US');
    const firstWeekStart = monthStart.startOf('week', { useLocaleWeeks: true });
    const lastWeekEnd = monthEnd.endOf('week', { useLocaleWeeks: true });
    if (queryFn) {
      await queryFn(firstWeekStart.toISODate() ?? undefined, lastWeekEnd.toISODate() ?? undefined);
    }
  };

  const onOpenStart = async () => {
    setOpenStartPicker(true);
    if (queryFn) await loadStatsFromDate(DateTime.now());
  };

  const onOpenEnd = async () => {
    setOpenEndPicker(true);
    if (queryFn) await loadStatsFromDate(DateTime.now());
  };

  const onMonthChange = async (newMonth: DateTime) => {
    if (queryFn) await loadStatsFromDate(newMonth);
  };

  const onYearChange = async (newYear: DateTime) => {
    if (queryFn) await loadStatsFromDate(newYear);
  };

  const handleEndDateChange = (newValue: DateTime | null | undefined) => {
    setEndDate(newValue ?? null);
    setOpenEndPicker(false);
  };

  return {
    startDate,
    endDate,
    openStartPicker,
    openEndPicker,
    setOpenEndPicker,
    setOpenStartPicker,
    onOpenStart,
    onOpenEnd,
    onMonthChange,
    onYearChange,
    handleStartAccepted,
    handleStartDateChange,
    handleEndDateChange,
  };
}

export default useDateRangePicker;
